// hooks/useSocket.js
import { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import { useAuthToken } from "./UseAuthToken";

const useSocket = (SERVER_BASE_URL) => {
  const socket = useRef(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [socketError, setSocketError] = useState(null);

  // 👇 single source of truth for auth token (passwordless OR Auth0)
  const token = useAuthToken();

  useEffect(() => {
    let newSocket = null;
    let alive = true;

    const initializeSocketConnection = async () => {
      // No token yet → don't try to connect
      if (!token) {
        setIsSocketConnected(false);
        // if we had a previous connection, clean it up
        if (socket.current) {
          socket.current.disconnect();
          socket.current = null;
        }
        return;
      }

      try {
        newSocket = io(SERVER_BASE_URL, {
          auth: { token },
        });

        newSocket.on("connect", () => {
          if (!alive) return;
          setIsSocketConnected(true);
          setSocketError(null);
        });

        newSocket.on("disconnect", () => {
          if (!alive) return;
          setIsSocketConnected(false);
        });

        newSocket.on("connect_error", (err) => {
          if (!alive) return;
          setIsSocketConnected(false);
          setSocketError(err.message);
        });

        socket.current = newSocket;
      } catch (error) {
        if (!alive) return;
        console.error("Socket connection error:", error);
        setSocketError("Failed to connect to the server.");
        setIsSocketConnected(false);
        socket.current = null;
      }
    };

    initializeSocketConnection();

    return () => {
      alive = false;
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [SERVER_BASE_URL, token]);

  return { socket, isSocketConnected, socketError };
};

export default useSocket;
